import { base64 } from "@scure/base";
import { ArchiveSteps } from "./ArchiveSteps";
import { AuctionSpotlight } from "./AuctionSpotlight";
import { DutchAuctionPrice, DutchAuctionSchedule } from "./DutchAuctionPrice";
import { dutchAuctionPresentation, dutchCountdown } from "./dutch-auction-presentation";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Eye,
  FileText,
  Gavel,
  Minus,
  Fingerprint,
  Layers,
  LoaderCircle,
  Menu,
  Plus,
  Search,

  ShieldCheck,
  Wallet,
  X,
} from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAppState } from "../../app-state";
import { ResilientInscriptionMedia } from "../../components/ResilientInscriptionMedia";
import {
  AlertLink,
  AlertsPage,
  AuctionArtActions,
  CatalogueGuide,
  GalleryCatalogue,
  GalleryEditor,
  GalleryPage,
  PassportPage,
} from "./ArtefactCommunity";
import {
  activateAuction,
  cancelAuction,
  closeAuction,
  createAuction,
  fetchAuction,
  fetchAuctions,
  fetchCreatorOwnedInscriptions,
  fetchMyAuctions,
  fetchUtxos,
  filterCardinalUtxos,
  quoteAuctionBid,
  recordAuctionBid,
} from "../../lib/api";
import {
  dutchAuctionPriceSats,
  nextAuctionBidSats,
  type AuctionBidQuote,
  type AuctionDetail,
  type AuctionInput,
  type AuctionPage,
  type AuctionPreparation,
  type AuctionRecord,
} from "../../lib/auctions";
import type { CreatorOwnedInscriptionPage } from "../../lib/launchpad";
import {
  keepCardinalUtxos,
  shortenAddress,
  signMultiplePsbts,
  signPsbtGroups,
  walletAvailability,
} from "../../lib/wallets";
import { transactionExplorerUrl } from "../../lib/explorer-links";
import {
  auctionDateTimeInputValue,
  parseAuctionDateTimeInput,
} from "../auctions/auction-date";

const ArtefactInscribe = lazy(() => import("./ArtefactInscribe").then(module => ({ default: module.ArtefactInscribe })));
const ArtefactGalleryInscribe = lazy(() => import("./ArtefactGalleryInscribe").then(module => ({ default: module.ArtefactGalleryInscribe })));
const ArtefactInscriptionComplete = lazy(() => import("./ArtefactInscribe").then(module => ({ default: module.ArtefactInscriptionComplete })));
const ArtefactInscriptionRecovery = lazy(() => import("./ArtefactInscribe").then(module => ({ default: module.ArtefactInscriptionRecovery })));

const integer = new Intl.NumberFormat("en-GB");
const sats = (value: number) => `${integer.format(value)} sats`;
const date = (value: number) =>
  new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
const message = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";

function useClock() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function timeLeft(endsAt: number, now: number) {
  const seconds = Math.max(0, Math.floor((endsAt - now) / 1000));
  if (seconds > 86400)
    return `${Math.floor(seconds / 86400)}d ${Math.floor(seconds / 3600) % 24}h`;
  return `${String(Math.floor(seconds / 3600)).padStart(2, "0")}h ${String(Math.floor(seconds / 60) % 60).padStart(2, "0")}m ${String(seconds % 60).padStart(2, "0")}s`;
}

function Alert({ children }: { children: ReactNode }) {
  return (
    <p className="ar-alert" role="alert">
      {children}
    </p>
  );
}
function Busy() {
  return (
    <span className="ar-loading" role="status">
      <LoaderCircle className="ar-spin" /> Loading the gallery…
    </span>
  );
}
function Mark() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path
        d="M4 28 16 4l12 24M10 20h13"
        fill="none" stroke="currentColor" strokeWidth="2.4"
      />
    </svg>
  );
}

function Dialog({
  open,
  close,
  title,
  children,
}: {
  open: boolean;
  close: () => void;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (open && !ref.current?.open) ref.current?.showModal();
    else if (!open) ref.current?.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="ar-dialog"
      aria-label={title}
      onCancel={close}
      onClose={close}
    >
      <header>
        <h2>{title}</h2>
        <button className="ar-icon" aria-label="Close dialog" onClick={close}>
          <X />
        </button>
      </header>
      {children}
    </dialog>
  );
}

function WalletDialog() {
  const state = useAppState();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const availability = walletAvailability();
  const close = useCallback(() => state.setWalletModalOpen(false), [state]);
  async function connect(provider: "xverse" | "unisat") {
    setBusy(true);
    setError("");
    try {
      await state.connectPrimaryWallet(provider);
      close();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open={state.walletModalOpen}
      close={close}
      title="Your wallet. Your collection."
    >
      <p>
        Connect your Bitcoin wallet. Verification is a free message signature,
        not a transaction. Your recovery phrase is never requested.
      </p>
      {state.wallet ? (
        <>
          <p className="ar-wallet-address">{state.wallet.ordinals.address}</p>
          <button
            className="ar-secondary"
            onClick={() => {
              state.disconnectWallet();
              close();
            }}
          >
            Disconnect wallet
          </button>
        </>
      ) : (
        <div className="ar-wallet-options">
          {(["xverse", "unisat"] as const).map((provider) => (
            <button
              key={provider}
              disabled={busy || !availability[provider]}
              onClick={() => void connect(provider)}
            >
              <Wallet />
              <span>
                {provider === "xverse" ? "Xverse" : "UniSat"}
                <small>
                  {availability[provider]
                    ? "Connect & verify"
                    : "Install the wallet extension first"}
                </small>
              </span>
              {busy ? <LoaderCircle className="ar-spin" /> : null}
            </button>
          ))}
        </div>
      )}
      {error ? <Alert>{error}</Alert> : null}
      <small className="ar-muted">
        Wallet ownership is verified with a signed message.
      </small>
    </Dialog>
  );
}

function Header() {
  const { wallet, linkedWallets, switchWallet, setWalletModalOpen } =
    useAppState();
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  useEffect(() => setMenu(false), [location.pathname]);
  return (
    <header className="ar-header">
      <Link className="ar-brand" to="/" aria-label="Artefact home">
        <Mark />
        artefact
      </Link>
      <nav
        className={menu ? "ar-nav open" : "ar-nav"}
        aria-label="Main navigation"
      >
        <NavLink to="/auctions">Auctions</NavLink>
        <NavLink to="/exhibitions">Exhibitions</NavLink>
        <NavLink to="/inscribe">Inscribe</NavLink>
        <NavLink to="/archive">For artists</NavLink>
        <NavLink to="/studio">Artist studio</NavLink>
        <AlertLink />
      </nav>
      <div className="ar-header-actions">
        {wallet && linkedWallets.length > 1 ? (
          <select
            aria-label="Active wallet"
            value={wallet.payment.address}
            onChange={(event) => switchWallet(event.target.value)}
          >
            {linkedWallets.map((item) => (
              <option key={item.payment.address} value={item.payment.address}>
                {shortenAddress(item.ordinals.address)}
              </option>
            ))}
          </select>
        ) : null}
        <button
          className="ar-wallet-button"
          onClick={() => setWalletModalOpen(true)}
        >
          <span className={wallet ? "ar-dot" : ""} />
          {wallet ? shortenAddress(wallet.ordinals.address) : "Connect wallet"}
          <Wallet />
        </button>

        <button
          className="ar-menu ar-icon"
          aria-label="Toggle menu"
          aria-expanded={menu}
          onClick={() => setMenu(!menu)}
        >
          {menu ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="ar-footer">
      <div>
        <Link className="ar-brand" to="/">
          <Mark />
          artefact
        </Link>
        <p>Art, for the long term.</p>
      </div>
      <div>
        <Link to="/auctions">Explore auctions</Link>
        <Link to="/archive">Archive your work</Link>
        <Link to="/exhibitions">Artist & collector galleries</Link>
        <Link to="/about">How it works</Link>
        <a
          href="https://spamthechain.com"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Bitcoin tools (opens in a new tab)"
          title="Opens in a new tab"
        >
          Bitcoin tools
        </a>
      </div>
      <small>
        Digital originals. Bitcoin mainnet. Made to outlast the moment.
      </small>
    </footer>
  );
}



function Home() {
  return (
    <>
      <section className="ar-hero">
        <div className="ar-hero-copy">
          <p className="ar-eyebrow">
            An independent art auction house / Bitcoin
          </p>
          <h1>
            Originals.
            <br />
            On record.
          </h1>
          <p>
            Discover art inscribed on Bitcoin. Collect an original, visit an
            exhibition, or bring your own work to auction.
          </p>
          <div className="ar-actions">
            <Link className="ar-primary" to="/auctions">
              Explore auctions
            </Link>
            <Link className="ar-text-link" to="/archive">
              For artists
            </Link>
          </div>
          <div className="ar-hero-note">
            <span className="ar-edition-label">ON BITCOIN</span>
            <span>
              The work lives on Bitcoin.
              <br />
              <b>The original belongs in your wallet.</b>
            </span>
          </div>
        </div>
        <AuctionSpotlight />
      </section>
      <div className="ar-principles">
        <span>
          <small>01</small> Art is the point.
        </span>
        <span>
          <small>02</small> Bitcoin is the medium.
        </span>
        <span>
          <small>03</small> Ownership is yours.
        </span>
        <Link to="/about">Discover Artefact</Link>
      </div>
      <section className="ar-artist-invite">
        <div>
          <p className="ar-eyebrow">For artists</p>
          <h2>
            Your work.
            <br />
            Your terms.
          </h2>
        </div>
        <div>
          <p>
            Archive an original on Bitcoin. Keep the art in your wallet until
            it sells. Choose an English or Dutch auction and set your terms.
          </p>
          <Link className="ar-primary" to="/studio">
            Enter your studio
          </Link>
        </div>
      </section>
      <AuctionFormats />
    </>
  );
}

function AuctionFormats() {
  return (
    <section className="ar-formats">
      <header>
        <p className="ar-eyebrow">Two ways to find its value</p>
        <h2>
          A little anticipation.
          <br />
          <em>A lasting connection.</em>
        </h2>
      </header>
      <article>
        <span className="ar-format-number">01 / ENGLISH</span>
        <div className="ar-price-diagram">
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
        <h3>The classic auction.</h3>
        <p>
          Collectors place signed bids before a fixed deadline. The highest
          valid bid wins. Losing bids never move Bitcoin.
        </p>
        <Link to="/auctions?format=english">Explore English auctions</Link>
      </article>
      <article>
        <span className="ar-format-number">02 / DUTCH</span>
        <div className="ar-price-diagram descending">
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
        <h3>Find your moment.</h3>
        <p>
          The price falls every minute toward the artist’s floor. The first
          accepted signed purchase wins, with immediate settlement submission.
        </p>
        <Link to="/auctions?format=dutch">Explore Dutch auctions</Link>
      </article>
    </section>
  );
}

function AuctionCard({
  auction,
  now,
}: {
  auction: AuctionRecord;
  now: number;
}) {
  const dutch = auction.kind === "dutch";
  const ended = auction.status !== "open" || auction.endsAt <= now;
  const price = dutch
    ? (auction.highBid?.bidSats ?? dutchAuctionPriceSats(auction, now))
    : (auction.highBid?.bidSats ?? auction.startPriceSats);
  const nextDrop = dutch ? dutchAuctionPresentation(auction, now).nextDrop : null;
  return (
    <Link to={`/auctions/${auction.id}`} className="ar-art-card">
      <div className="ar-artwork">
        <ResilientInscriptionMedia
          inscriptionId={auction.inscriptionId}
          label={auction.title}
          className="ar-media"
        />
        <span className="ar-card-view">
          <Eye /> View work
        </span>
      </div>
      <div className="ar-card-title">
        <div>
          <span className="ar-card-meta">
            <span>{dutch ? "Dutch auction" : "English auction"}</span>
            <small>
              {auction.collectionName && auction.collectionName !== "Uncollected"
                ? auction.collectionName
                : "Bitcoin original"}
            </small>
          </span>
          <h3>{auction.title}</h3>
        </div>
      </div>
      <div className="ar-card-stats">
        <span>
          <small>
            {ended
              ? auction.highBid
                ? "Final price"
                : "Unsold"
              : dutch
                ? "Current price"
                : auction.highBid
                  ? "Highest bid"
                  : "Opening bid"}
          </small>
          <b>{ended && !auction.highBid ? "—" : sats(price)}</b>
        </span>
        <span>
          <small>
            {ended
              ? "Ended"
              : auction.startsAt > now
                ? "Starts"
                : "Time remaining"}
          </small>
          <b>
            {ended || auction.startsAt > now
              ? date(ended ? auction.endsAt : auction.startsAt)
              : timeLeft(auction.endsAt, now)}
          </b>
        </span>
      </div>
      {nextDrop ? <div className="ar-card-drop">
        <span><ArrowDown aria-hidden="true" /> Next {sats(nextDrop.price)}</span>
        <span>in {dutchCountdown(nextDrop.at, now)}</span>
      </div> : null}
    </Link>
  );
}

function AuctionCatalogue({
  mine = false,
}: {
  mine?: boolean;
}) {
  const { authorization, wallet, authorizeWallet, setWalletModalOpen } =
    useAppState();
  const [params, setParams] = useSearchParams();
  const status = params.get("view") === "ended" ? "ended" : "open";
  const format = params.get("format") ?? "all";
  const [page, setPage] = useState(0);
  const [data, setData] = useState<AuctionPage | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const now = useClock();
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAppliedQuery(query);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    if (mine && (!wallet || !authorization)) {
      setLoading(false);
      setData(null);
      return;
    }
    const filters = { format, query: appliedQuery };
    const request = mine
      ? fetchMyAuctions(authorization!, page, filters)
      : fetchAuctions(page, authorization ?? undefined, status, filters);
    void request
      .then((next) => {
        if (active) setData(next);
      })
      .catch((caught: unknown) => {
        if (active) setError(message(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    const timer = window.setTimeout(() => {
      if (active) setRefresh((value) => value + 1);
    }, 30_000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    appliedQuery,
    authorization,
    format,
    mine,
    page,
    refresh,
    status,
    wallet,
  ]);
  const filtered = data?.auctions ?? [];
  return (
    <section className="ar-catalogue">
      <header className="ar-section-heading">
        <div>
          <p className="ar-eyebrow">
            {mine ? "Your artist desk" : "The auction room"}
          </p>
          <h2>
            {mine
              ? "Your auctions."
              : status === "ended"
                ? "Past auctions."
                : "Current & forthcoming."}
          </h2>
        </div>
        <Link className="ar-primary" to="/studio/new">
          Create an auction <Plus />
        </Link>
      </header>
      <div className="ar-catalogue-controls">
        <nav aria-label="Auction views">
          {mine ? (
            <span className="ar-muted">Your works, past and present</span>
          ) : (
            (["open", "ended"] as const).map((view) => (
              <button
                key={view}
                className={status === view ? "active" : ""}
                onClick={() => {
                  setParams({ view, format });
                  setPage(0);
                }}
              >
                {view === "open" ? "Live & upcoming" : "Archive"}
              </button>
            ))
          )}
        </nav>
        <div>
          <label className="ar-search">
            <Search />
            <input
              aria-label="Search auctions"
              placeholder="Find a work…"
              maxLength={100}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label className="ar-select">
            <select
              aria-label="Auction format"
              value={format}
              onChange={(event) => {
                setParams({ view: status, format: event.target.value });
                setPage(0);
              }}
            >
              <option value="all">All formats</option>
              <option value="english">English auctions</option>
              <option value="dutch">Dutch auctions</option>
            </select>
            <ChevronDown />
          </label>
        </div>
      </div>
      {error ? (
        <>
          <Alert>{error}</Alert>
          <button
            className="ar-secondary"
            onClick={() => setRefresh((value) => value + 1)}
          >
            Try again
          </button>
        </>
      ) : loading && !data ? (
        <Busy />
      ) : mine && !authorization ? (
        <div className="ar-empty">
          <Wallet />
          <h3>Your studio starts with your wallet.</h3>
          <p>Verify ownership to see your auctions.</p>
          <button
            className="ar-primary"
            onClick={() =>
              wallet
                ? void authorizeWallet(true).catch((caught: unknown) =>
                    setError(message(caught)),
                  )
                : setWalletModalOpen(true)
            }
          >
            {wallet ? "Verify wallet" : "Connect wallet"}
          </button>
        </div>
      ) : filtered.length ? (
        <div className="ar-art-grid">
          {filtered.map((auction) => (
            <AuctionCard key={auction.id} auction={auction} now={now} />
          ))}
        </div>
      ) : (
        <div className="ar-empty">
          <span className="ar-empty-mark">
            <Mark />
          </span>
          <p className="ar-eyebrow">
            {query || format !== "all"
              ? "Nothing here just yet"
              : "Every collection begins somewhere"}
          </p>
          <h3>
            {query || format !== "all"
              ? "No works match this view."
              : "The first chapter could be yours."}
          </h3>
          <p>
            {query || format !== "all"
              ? "Try another auction format or a different search."
              : "This is Artefact’s own auction room. No imported listings, no invented sales. Just a space waiting for your original work."}
          </p>
          <Link className="ar-text-link" to="/studio/new">
            Bring your work to Artefact
          </Link>
        </div>
      )}
      {data && data.total > data.pageSize ? (
        <div className="ar-pagination">
          <button
            disabled={page === 0 || loading}
            onClick={() => setPage(page - 1)}
          >
            <ArrowLeft /> Previous
          </button>
          <span>
            Page {page + 1} of {Math.ceil(data.total / data.pageSize)}
          </span>
          <button
            disabled={(page + 1) * data.pageSize >= data.total || loading}
            onClick={() => setPage(page + 1)}
          >
            Next <ArrowRight />
          </button>
        </div>
      ) : null}
    </section>
  );
}

function AuctionDetailPage() {
  const { auctionId = "" } = useParams();
  const {
    wallet,
    authorization,
    authorizeWallet,
    fees,
    config,
    setWalletModalOpen,
  } = useAppState();
  const [auction, setAuction] = useState<AuctionDetail | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("");
  const [quote, setQuote] = useState<AuctionBidQuote | null>(null);
  const [refresh, setRefresh] = useState(0);
  const now = useClock();
  useEffect(() => {
    let active = true;
    void fetchAuction(auctionId, authorization ?? undefined)
      .then((next) => {
        if (active) {
          setAuction(next);
          setError("");
        }
      })
      .catch((caught: unknown) => {
        if (active) setError(message(caught));
      });
    const timer = window.setTimeout(
      () => setRefresh((value) => value + 1),
      15_000,
    );
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [auctionId, authorization, refresh]);
  useEffect(() => {
    setQuote(null);
  }, [wallet?.payment.address, auctionId]);
  if (!auction)
    return (
      <main className="ar-page">
        {error ? (
          <>
            <Alert>{error}</Alert>
            <Link to="/auctions">Back to auctions</Link>
          </>
        ) : (
          <Busy />
        )}
      </main>
    );
  const dutch = auction.kind === "dutch";
  const minimum = dutch
    ? dutchAuctionPriceSats(auction, now)
    : nextAuctionBidSats(auction);
  const open =
    auction.status === "open" &&
    auction.settlementState === "open" &&
    auction.startsAt <= now &&
    auction.endsAt > now &&
    !(dutch && auction.highBid);
  async function review(event: FormEvent) {
    event.preventDefault();
    if (!wallet) {
      setWalletModalOpen(true);
      return;
    }
    setBusy(true);
    setError("");
    setQuote(null);
    setNotice("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet first");
      const utxos = await filterCardinalUtxos(
        await keepCardinalUtxos(
          wallet,
          await fetchUtxos(wallet.payment.address),
        ),
      );
      setQuote(
        await quoteAuctionBid(
          token,
          auctionId,
          dutch ? 0 : Number(amount || minimum),
          Number(rate || fees?.halfHourFee || 1),
          utxos,
        ),
      );
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  async function sign() {
    if (!wallet || !quote || quote.expiresAt <= Date.now()) return;
    setBusy(true);
    setError("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet first");
      const signed = await signPsbtGroups(
        wallet,
        base64.decode(quote.psbtBase64),
        { [wallet.payment.address]: quote.paymentInputIndexes },
      );
      const result = await recordAuctionBid(
        token,
        auctionId,
        quote.reservationId,
        base64.encode(signed),
      );
      setAuction(result.auction);
      setQuote(null);
      setNotice(
        result.auction.settlementState === "settled"
          ? "Your acquisition has been submitted on Bitcoin. Follow the transaction for confirmation."
          : dutch
            ? "Your purchase authorization is accepted. Settlement is processing; Bitcoin confirmation can take longer."
            : "Your bid is authorized. No Bitcoin moves unless your valid bid wins.",
      );
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  async function manage(action: "cancel" | "close") {
    setBusy(true);
    setError("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet first");
      setAuction(
        action === "cancel"
          ? await cancelAuction(token, auctionId)
          : await closeAuction(token, auctionId),
      );
      setNotice(
        action === "cancel"
          ? "Auction cancelled."
          : "Settlement status refreshed.",
      );
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="ar-page">
      <Link className="ar-back" to="/auctions">
        <ArrowLeft /> The auction room
      </Link>
      <div className="ar-detail-grid">
        <div>
          <div className="ar-detail-art">
            <ResilientInscriptionMedia
              inscriptionId={auction.inscriptionId}
              label={auction.title}
              className="ar-media"
              priority
            />
          </div>
          <div className="ar-art-provenance">
            <Fingerprint />
            <span>
              Inscribed on Bitcoin
              <br />
              <a
                href={`https://ordinals.com/inscription/${auction.inscriptionId}`}
                target="_blank"
                rel="noreferrer"
              >
                View the on-chain original
              </a>
            </span>
            <small>
              {auction.inscriptionNumber === null
                ? ""
                : `№ ${integer.format(auction.inscriptionNumber)}`}
            </small>
          </div>
          <AuctionArtActions auction={auction} />
        </div>
        <section className="ar-detail-info">
          <p className="ar-eyebrow">
            {dutch
              ? "Dutch auction · descending price"
              : "English auction · highest valid bid wins"}
          </p>
          <h1>{auction.title}</h1>
          <p className="ar-description">
            {auction.description || "An original work, archived on Bitcoin."}
          </p>
          <div className="ar-seller">
            <span>Seller’s verified wallet</span>
            <a
              href={`https://mempool.space/address/${auction.sellerOrdinalsAddress}`}
              target="_blank"
              rel="noreferrer"
            >
              {shortenAddress(auction.sellerOrdinalsAddress)}
            </a>
          </div>
          {dutch ? <DutchAuctionPrice auction={auction} now={now} /> : <div className="ar-price">
            <small>
              {auction.status !== "open"
                ? auction.highBid
                  ? "Final price"
                  : "Closed without a sale"
                : dutch
                  ? "Current asking price"
                  : auction.highBid
                    ? "Highest bid"
                    : "Opening bid"}
            </small>
            <strong>{sats(auction.highBid?.bidSats ?? minimum)}</strong>
          </div>}
          <div className="ar-time">
            <Clock3 />
            <span>
              {auction.startsAt > now
                ? `Opens ${date(auction.startsAt)}`
                : auction.endsAt > now && auction.status === "open"
                  ? `Ends in ${timeLeft(auction.endsAt, now)}`
                  : `Ended ${date(auction.endsAt)}`}
            </span>
            <small>
              {auction.settlementState === "open"
                ? auction.status
                : auction.settlementState}
            </small>
          </div>
          {notice ? (
            <p className="ar-success" role="status">
              <CheckCircle2 /> {notice}
            </p>
          ) : null}
          {error ? <Alert>{error}</Alert> : null}
          {auction.canManage ? (
            <div className="ar-owner-actions">
              <p>This is your work. You cannot bid on your own auction.</p>
              {auction.status === "open" && auction.bidCount === 0 ? (
                <button
                  className="ar-secondary"
                  disabled={busy}
                  onClick={() => void manage("cancel")}
                >
                  Cancel auction
                </button>
              ) : null}
              {auction.endsAt <= now &&
              auction.status === "open" &&
              config?.transactionsEnabled ? (
                <button
                  className="ar-primary"
                  disabled={busy}
                  onClick={() => void manage("close")}
                >
                  Request settlement <ArrowRight />
                </button>
              ) : null}
            </div>
          ) : open ? (
            <form className={dutch ? "ar-dutch-buy" : undefined} onSubmit={(event) => void review(event)}>
              {!dutch ? (
                <label className="ar-field">
                  Your bid, in sats
                  <input
                    type="number"
                    min={minimum}
                    max={auction.buyNowSats ?? undefined}
                    step="1"
                    value={amount}
                    placeholder={String(minimum)}
                    onChange={(event) => {
                      setAmount(event.target.value);
                      setQuote(null);
                    }}
                  />
                </label>
              ) : null}
              <details className="ar-network">
                <summary>
                  Network fee preference <ChevronDown />
                </summary>
                <label className="ar-field">
                  Miner fee rate (sat/vB)
                  <input
                    type="number"
                    min="0.1"
                    max="1000"
                    step="0.1"
                    value={rate}
                    placeholder={String(fees?.halfHourFee ?? 1)}
                    onChange={(event) => {
                      setRate(event.target.value);
                      setQuote(null);
                    }}
                  />
                </label>
              </details>
              <button
                className="ar-primary ar-wide"
                disabled={busy}
                type="submit"
              >
                {busy ? <LoaderCircle className="ar-spin" /> : null}
                {wallet
                  ? dutch
                    ? "Buy now"
                    : "Review bid"
                  : dutch ? "Connect to buy" : "Connect to collect"}
              </button>
              {!dutch && auction.buyNowSats ? (
                <button
                  className="ar-text-link"
                  type="button"
                  onClick={() => {
                    setAmount(String(auction.buyNowSats));
                    setQuote(null);
                  }}
                >
                  Set buy-now amount: {sats(auction.buyNowSats)}
                </button>
              ) : null}
              <p className="ar-fineprint">
                {dutch
                  ? "Your exact price is fixed in the review for up to 60 seconds. A quote does not reserve the artwork; the first accepted signed purchase wins."
                  : "Bids are signed authorizations, not deposits. Keep the quoted Bitcoin outputs unspent to remain eligible. Fixed deadline; no automatic extensions."}
              </p>
            </form>
          ) : (
            <div className="ar-closed">
              <ShieldCheck />
              <p>
                {dutch && auction.highBid && auction.status === "open"
                  ? "A buyer has been accepted. Settlement is processing."
                  : auction.startsAt > now
                    ? "This auction has not opened yet."
                    : "This auction is no longer accepting bids."}
              </p>
            </div>
          )}
          {dutch ? <DutchAuctionSchedule auction={auction} now={now} /> : null}
          {auction.settlementTxid ? (
            <a
              className="ar-text-link"
              href={transactionExplorerUrl(auction.settlementTxid)}
              target="_blank"
              rel="noreferrer"
            >
              <FileText /> Track the Bitcoin transaction
            </a>
          ) : null}
          <details className="ar-trust">
            <summary>
              Ownership, fees & settlement <Plus />
            </summary>
            <p>
              Your work remains in your wallet until a sale. Settlement uses a
              platform-assisted, signed-transaction mechanism: artwork and
              seller payment move atomically. It is not an independently
              trustless protocol.
            </p>
            <p>
              The seller pays {auction.serviceFeeBps / 100}% with a{" "}
              {sats(auction.minimumServiceFeeSats)} minimum, deducted from the
              sale price. The winner also pays the Bitcoin package miner fee.
              Losing bidders pay nothing. Bitcoin confirmation times are not
              guaranteed.
            </p>
          </details>
        </section>
      </div>
      <section className="ar-bid-history">
        <header className="ar-section-heading">
          <h2>{dutch ? "Acquisition activity" : "Bidding history"}</h2>
          <span>{auction.bidCount} authorizations</span>
        </header>
        {auction.bids.length ? (
          auction.bids.map((bid) => (
            <article key={bid.id}>
              <span>
                <Wallet />
                {bid.bidderProfileUsername
                  ? `@${bid.bidderProfileUsername}`
                  : shortenAddress(bid.bidderOrdinalsAddress)}
              </span>
              <span>{date(bid.createdAt)}</span>
              <strong>{sats(bid.bidSats)}</strong>
              <small>{bid.status}</small>
            </article>
          ))
        ) : (
          <p className="ar-muted">
            {dutch ? "No purchases yet. The first accepted signed purchase wins." : "No bids yet. A good beginning is still ahead."}
          </p>
        )}
      </section>
      <Dialog
        open={Boolean(quote)}
        close={() => {
          if (!busy) setQuote(null);
        }}
        title={dutch ? "Review your acquisition" : "Review your bid"}
      >
        {quote ? (
          <>
            <p>{auction.title}</p>
            <dl className="ar-quote">
              <div>
                <dt>{dutch ? "Purchase price" : "Bid authorization"}</dt>
                <dd>{sats(quote.bidSats)}</dd>
              </div>
              <div>
                <dt>Bitcoin package fee</dt>
                <dd>{sats(quote.networkFeeSats)}</dd>
              </div>
              <div>
                <dt>Total from your wallet</dt>
                <dd>{sats(quote.totalSats)}</dd>
              </div>
              <div>
                <dt>Artist receives after platform fee</dt>
                <dd>{sats(quote.sellerReceivesSats)}</dd>
              </div>
            </dl>
            <p className="ar-wallet-address">
              Artwork destination: {wallet?.ordinals.address}
            </p>
            <p className="ar-fineprint">
              {dutch
                ? "Signing authorizes this purchase at the exact price above, even if the displayed Dutch price decreases. The first accepted signed purchase wins."
                : "Signing authorizes settlement only if your valid bid wins. Nothing is paid for a losing bid."}
            </p>
            <p className="ar-fineprint">
              Quote expires {date(quote.expiresAt)}.
              {!config?.transactionsEnabled
                ? " Staging: Bitcoin broadcasts are disabled."
                : ""}
            </p>
            <button
              className="ar-primary ar-wide"
              disabled={busy || quote.expiresAt <= now || !open}
              onClick={() => void sign()}
            >
              {busy ? <LoaderCircle className="ar-spin" /> : <ShieldCheck />}
              {!open
                ? "Auction no longer available"
                : quote.expiresAt <= now
                  ? "Quote expired — close and refresh"
                  : dutch
                    ? "Sign purchase"
                    : "Sign bid authorization"}
            </button>
            {error ? <Alert>{error}</Alert> : null}
          </>
        ) : null}
      </Dialog>
    </main>
  );
}

function StudioCreate() {
  const { wallet, authorization, authorizeWallet, setWalletModalOpen } =
    useAppState();
  const navigate = useNavigate();
  const [inventory, setInventory] =
    useState<CreatorOwnedInscriptionPage | null>(null);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [prepared, setPrepared] = useState<AuctionPreparation | null>(null);
  const [input, setInput] = useState<AuctionInput>(() => ({
    platform: "artefact",
    kind: "english",
    title: "",
    description: "",
    inscriptionId: "",
    startPriceSats: 100000,
    minIncrementSats: 1000,
    floorPriceSats: 10000,
    startsAt: Date.now() + 120000,
    endsAt: Date.now() + 86400000,
  }));
  const now = useClock();
  useEffect(() => {
    if (!authorization || !wallet) return;
    let active = true;
    setLoading(true);
    void fetchCreatorOwnedInscriptions(authorization, page, query)
      .then((next) => {
        if (active) setInventory(next);
      })
      .catch((caught: unknown) => {
        if (active) setError(message(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authorization, wallet, page, query, refresh]);
  useEffect(() => {
    setPrepared(null);
    setInventory(null);
    setInput((value) => ({ ...value, inscriptionId: "" }));
  }, [wallet?.ordinals.address]);
  async function prepare(event: FormEvent) {
    event.preventDefault();
    if (!wallet) {
      setWalletModalOpen(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet first");
      setPrepared(
        await createAuction(token, {
          ...input,
          floorPriceSats: input.kind === "dutch" ? input.floorPriceSats : null,
        }),
      );
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  async function discardPreparation() {
    if (!prepared) return;
    setBusy(true);
    setError("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet first");
      await cancelAuction(token, prepared.auctionId);
      setPrepared(null);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  async function publish() {
    if (!wallet || !prepared) return;
    setBusy(true);
    setError("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet first");
      const signed = await signMultiplePsbts(
        wallet,
        prepared.psbts.map((psbt) => ({
          key: psbt.key,
          psbtBase64: psbt.psbtBase64,
          address: psbt.signingAddress,
          inputIndexes: psbt.signingIndexes,
          useTweakedSigner: true,
        })),
        "Authorize your Artefact auction",
      );
      if (!signed.listing)
        throw new Error("The wallet did not return the listing signature");
      const auction = await activateAuction(
        token,
        prepared.auctionId,
        signed.listing,
      );
      await navigate(`/auctions/${auction.id}`);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="ar-page">
      <Link className="ar-back" to="/studio">
        <ArrowLeft /> Your studio
      </Link>
      <header className="ar-page-heading">
        <p className="ar-eyebrow">Let the work speak</p>
        <h1>
          Give it a <em>stage.</em>
        </h1>
        <p>
          Choose an inscribed original from your wallet. Set the terms. Review
          before you sign.
        </p>
      </header>
      {!wallet || !authorization ? (
        <div className="ar-empty">
          <Wallet />
          <h3>Start with your artist wallet.</h3>
          <p>
            We’ll verify the wallet that holds your art and load your profile
            and linked wallets.
          </p>
          <button
            className="ar-primary"
            onClick={() =>
              wallet
                ? void authorizeWallet(true).catch((caught: unknown) =>
                    setError(message(caught)),
                  )
                : setWalletModalOpen(true)
            }
          >
            {wallet ? "Verify ownership" : "Connect wallet"}
            <ArrowRight />
          </button>
        </div>
      ) : (
        <div className="ar-create-grid">
          <section className="ar-inventory">
            <header>
              <h2>Choose your work</h2>
              <button
                className="ar-icon"
                aria-label="Refresh artwork inventory"
                onClick={() => setRefresh((value) => value + 1)}
              >
                <ArrowDown />
              </button>
            </header>
            <form
              className="ar-search"
              onSubmit={(event) => {
                event.preventDefault();
                setPage(0);
                setQuery(search);
              }}
            >
              <Search />
              <input
                aria-label="Search wallet artwork"
                placeholder="Title, inscription or collection"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <button type="submit">Find</button>
            </form>
            {loading ? (
              <Busy />
            ) : inventory?.items.length ? (
              <div className="ar-owned-grid">
                {inventory.items.map((item) => (
                  <button
                    key={item.inscriptionId}
                    disabled={busy || Boolean(prepared)}
                    className={
                      input.inscriptionId === item.inscriptionId
                        ? "selected"
                        : ""
                    }
                    onClick={() => {
                      setInput((value) => ({
                        ...value,
                        inscriptionId: item.inscriptionId,
                        title: item.name.slice(0, 80),
                      }));
                      setPrepared(null);
                    }}
                  >
                    <ResilientInscriptionMedia
                      inscriptionId={item.inscriptionId}
                      label={item.name}
                      className="ar-media"
                    />
                    <span>
                      {item.name}
                      {input.inscriptionId === item.inscriptionId ? (
                        <Check />
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="ar-small-empty">
                <Layers />
                <p>No matching inscribed art in this wallet.</p>
                <Link to="/archive">Archive a new work</Link>
              </div>
            )}
            {inventory && inventory.total > inventory.pageSize ? (
              <div className="ar-pagination">
                <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                  Previous
                </button>
                <span>Page {page + 1}</span>
                <button
                  disabled={(page + 1) * inventory.pageSize >= inventory.total}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>
          <form
            className="ar-auction-form"
            onSubmit={(event) => void prepare(event)}
          >
            <p className="ar-eyebrow">Your work. Your terms.</p>
            <h2>Create an auction</h2>
            <fieldset disabled={busy || Boolean(prepared)}>
              <legend className="ar-sr-only">Auction terms</legend>
              <div className="ar-format-picker">
                {(["english", "dutch"] as const).map((kind) => (
                  <button
                    type="button"
                    aria-pressed={input.kind === kind}
                    className={input.kind === kind ? "active" : ""}
                    key={kind}
                    onClick={() =>
                      setInput({ ...input, kind, buyNowSats: null })
                    }
                  >
                    {kind === "english" ? <Gavel /> : <Minus />}
                    <span>
                      {kind === "english" ? "English auction" : "Dutch auction"}
                      <small>
                        {kind === "english"
                          ? "Bids rise to a fixed deadline"
                          : "Price falls to your floor"}
                      </small>
                    </span>
                  </button>
                ))}
              </div>
              <label className="ar-field">
                Artwork title
                <input
                  required
                  maxLength={80}
                  value={input.title}
                  onChange={(event) =>
                    setInput({ ...input, title: event.target.value })
                  }
                  placeholder="Give your work a name"
                />
              </label>
              <label className="ar-field">
                Inscription ID
                <input
                  required
                  pattern="[a-fA-F0-9]{64}i[0-9]+"
                  value={input.inscriptionId}
                  onChange={(event) =>
                    setInput({
                      ...input,
                      inscriptionId: event.target.value.trim(),
                    })
                  }
                  placeholder="Select a work or paste its inscription ID"
                />
              </label>
              <label className="ar-field">
                The story behind the work
                <textarea
                  maxLength={2000}
                  rows={4}
                  value={input.description}
                  onChange={(event) =>
                    setInput({ ...input, description: event.target.value })
                  }
                  placeholder="Tell collectors what this work means to you…"
                />
              </label>
              <div className="ar-field-grid">
                <label className="ar-field">
                  {input.kind === "dutch"
                    ? "Starting price (sats)"
                    : "Opening bid (sats)"}
                  <input
                    type="number"
                    min="3000"
                    required
                    value={input.startPriceSats}
                    onChange={(event) =>
                      setInput({
                        ...input,
                        startPriceSats: Number(event.target.value),
                      })
                    }
                  />
                </label>
                {input.kind === "dutch" ? (
                  <label className="ar-field">
                    Floor price (sats)
                    <input
                      type="number"
                      min="3000"
                      max={input.startPriceSats - 1}
                      required
                      value={input.floorPriceSats ?? ""}
                      onChange={(event) =>
                        setInput({
                          ...input,
                          floorPriceSats: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                ) : (
                  <label className="ar-field">
                    Minimum bid increment (sats)
                    <input
                      type="number"
                      min="1"
                      required
                      value={input.minIncrementSats}
                      onChange={(event) =>
                        setInput({
                          ...input,
                          minIncrementSats: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                )}
                <label className="ar-field">
                  Opens
                  <input
                    type="datetime-local"
                    required
                    value={auctionDateTimeInputValue(input.startsAt)}
                    onChange={(event) => {
                      const value = parseAuctionDateTimeInput(
                        event.target.value,
                      );
                      if (value !== null)
                        setInput({ ...input, startsAt: value });
                    }}
                  />
                </label>
                <label className="ar-field">
                  Closes
                  <input
                    type="datetime-local"
                    required
                    value={auctionDateTimeInputValue(input.endsAt)}
                    onChange={(event) => {
                      const value = parseAuctionDateTimeInput(
                        event.target.value,
                      );
                      if (value !== null) setInput({ ...input, endsAt: value });
                    }}
                  />
                </label>
              </div>
            </fieldset>
            <p className="ar-fineprint">
              <ShieldCheck /> Signing authorizes a platform-assisted atomic
              sale, not an immediate transfer. The work stays in your wallet
              until settlement. Seller fee: 2%, minimum 1,500 sats, deducted
              from proceeds. The winner pays miner fees.
            </p>
            {error ? <Alert>{error}</Alert> : null}
            {prepared ? (
              <div className="ar-prepared">
                <CheckCircle2 />
                <h3>Review before publishing</h3>
                <p>
                  {input.title} ·{" "}
                  {input.kind === "dutch"
                    ? `Dutch: ${sats(input.startPriceSats)} → ${sats(input.floorPriceSats!)}`
                    : `English: opens at ${sats(input.startPriceSats)}`}
                </p>
                <p>
                  Opens {date(input.startsAt)}
                  <br />
                  Closes {date(input.endsAt)}
                </p>
                <p className="ar-wallet-address">
                  Seller payment: {wallet.payment.address}
                  <br />
                  Artwork: {prepared.inscriptionId}
                </p>
                <button
                  type="button"
                  className="ar-primary ar-wide"
                  disabled={busy || prepared.expiresAt <= now}
                  onClick={() => void publish()}
                >
                  {busy ? (
                    <LoaderCircle className="ar-spin" />
                  ) : (
                    <ShieldCheck />
                  )}
                  {prepared.expiresAt <= now
                    ? "Review expired"
                    : "Sign & publish auction"}
                </button>
                <button
                  className="ar-text-link"
                  type="button"
                  disabled={busy}
                  onClick={() => void discardPreparation()}
                >
                  Back to terms
                </button>
              </div>
            ) : (
              <button
                className="ar-primary ar-wide"
                type="submit"
                disabled={busy || !input.inscriptionId}
              >
                {busy ? <LoaderCircle className="ar-spin" /> : null}Review
                auction <ArrowRight />
              </button>
            )}
          </form>
        </div>
      )}
      {error && (!wallet || !authorization) ? <Alert>{error}</Alert> : null}
    </main>
  );
}

function ArchivePage() {
  return (
    <main className="ar-page ar-archive-page">
      <section className="ar-archive-hero" aria-labelledby="archive-heading">
        <header className="ar-archive-copy">
          <h1 id="archive-heading">The archive.</h1>
          <p className="ar-archive-deck">A guide to publishing an original on Bitcoin.</p>
          <Link
            className="ar-primary"
            to="/inscribe"
          >
            Inscribe your work <ArrowRight aria-hidden="true" />
          </Link>
          <nav aria-label="Artist next steps" className="ar-archive-links">
            <Link to="/galleries/new?kind=artist">Create an artist archive.</Link>
          </nav>
          <ArchiveSteps />
        </header>
        <AuctionSpotlight />
      </section>
      <aside className="ar-archive-signing-note">
        <p>Inscribe directly on Artefact. Review your file, costs and destination before you sign. No Bitcoin moves just by visiting this page. Only publish work you have the rights to use.</p>
        <Link to="/studio/new">Already inscribed? Create an auction <ArrowRight aria-hidden="true" /></Link>
      </aside>
      <section className="ar-archive-note">
        <div>
          <p className="ar-eyebrow">A note on provenance</p>
          <h3>Know what is on chain.</h3>
          <p>
            Inscription content and provenance are on-chain. Auction titles,
            descriptions and bids are indexed by our shared backend; they are
            not themselves immutable inscription metadata. A wallet proves
            ownership, not authorship. Collect thoughtfully.
          </p>
        </div>
      </section>
      <CatalogueGuide />
    </main>
  );
}

function AboutPage() {
  return (
    <main className="ar-page">
      <header className="ar-page-heading ar-editorial-heading">
        <p className="ar-eyebrow">A different kind of permanence</p>
        <h1>
          Less noise.
          <br />
          <em>More meaning.</em>
        </h1>
        <p>
          Artefact is an art-first auction house built around Bitcoin originals.
          A considered space for artists, collectors and the work between them.
        </p>
      </header>
      <AuctionFormats />
      <section className="ar-faq">
        <h2>The important details.</h2>
        {[
          [
            "Do I need a new wallet?",
            "No. Connect Xverse or UniSat. Artefact asks you to verify wallet ownership on this site; it does not silently copy another website’s login.",
          ],
          [
            "Does my artwork leave my wallet when I list it?",
            "No. You sign a future settlement authorization. The art remains in your wallet until a sale package is broadcast. The mechanism is platform-assisted: the platform validates and co-signs settlement. It is not a fully trustless auction protocol.",
          ],
          [
            "What happens to losing bids?",
            "There is nothing to refund. Bids are signed authorizations, not deposits. Bitcoin moves only for a successful winner. Spending your quoted payment outputs elsewhere invalidates your bid; the highest remaining valid authorization can then win.",
          ],
          [
            "How does a Dutch auction work?",
            "The artist sets a starting price, a lower floor and the opening/closing times. The asking price descends in one-minute steps. A quote fixes the exact signing price for at most 60 seconds but does not reserve the work. The first accepted signed purchase wins. Settlement submission starts immediately in production; Bitcoin confirmation takes longer.",
          ],
          [
            "Are there last-minute extensions?",
            "No. English auctions have a fixed advertised deadline. Automatic settlement checks run roughly every three minutes after closing. Network availability and Bitcoin confirmation can add time; there is no guaranteed maximum settlement time.",
          ],
          [
            "What are the fees?",
            "Seller commission is 2% of the winning price, with a 1,500-sat minimum, deducted from the proceeds. The winning buyer pays the Bitcoin package network fee, including a 21-sat listing-parent fee. Inscribing new art is a separate, priced action. The transaction review shows exact amounts before signing.",
          ],
          [
            "What if an artwork does not sell?",
            "No auction payment or miner fee is taken. The art stays in the seller’s wallet. You can cancel before a signed bid exists, or let an unsold auction close. Moving the art yourself invalidates the pending auction.",
          ],
        ].map(([title, body]) => (
          <details key={title}>
            <summary>
              {title}
              <Plus />
            </summary>
            <p>{body}</p>
          </details>
        ))}
      </section>
    </main>
  );
}

export function ArtefactApp() {
  const { config } = useAppState();
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return (
    <div className="artefact-app">
      <a className="ar-skip" href="#artefact-content">
        Skip to content
      </a>
      <Header />
      {config?.deploymentEnvironment === "staging" ? (
        <p className="ar-environment" role="status">
          Staging preview · Auction and inscription broadcasts are disabled.
        </p>
      ) : null}
      <div id="artefact-content">
        <Suspense fallback={<main className="ar-page" role="status">Opening the artist's workbench…</main>}>
        <Routes>
          <Route path="inscribe" element={<ArtefactInscribe />} />
          <Route path="inscribe/gallery" element={<ArtefactGalleryInscribe />} />
          <Route path="inscribe/complete" element={<ArtefactInscriptionComplete />} />
          <Route path="inscribe/recovery" element={<ArtefactInscriptionRecovery />} />
          <Route
            index
            element={
              <main>
                <Home />
              </main>
            }
          />
          <Route
            path="auctions"
            element={
              <main className="ar-page">
                <AuctionCatalogue />
              </main>
            }
          />
          <Route path="auctions/:auctionId" element={<AuctionDetailPage />} />
          <Route path="exhibitions" element={<GalleryCatalogue />} />
          <Route path="my-galleries" element={<GalleryCatalogue mine />} />
          <Route path="galleries/new" element={<GalleryEditor />} />
          <Route path="galleries/:galleryId" element={<GalleryPage />} />
          <Route path="galleries/:galleryId/edit" element={<GalleryEditor />} />
          <Route path="artworks/:inscriptionId" element={<PassportPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route
            path="studio"
            element={
              <main className="ar-page">
                <nav className="ar-studio-links" aria-label="Studio sections">
                  <Link to="/my-galleries">My galleries & archive</Link>
                  <Link to="/galleries/new">Create an exhibition</Link>
                  <Link to="/alerts">Private watchlist</Link>
                </nav>
                <AuctionCatalogue mine />
              </main>
            }
          />
          <Route path="studio/new" element={<StudioCreate />} />
          <Route path="archive" element={<ArchivePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
      <Footer />
      <WalletDialog />
    </div>
  );
}
